/**
 * `help/HelpGuideDrawer.tsx` — one in-app guide, in the non-modal side
 * drawer (`SideDrawer`, 480 px). The page stays usable behind it, so the
 * lead can read a step and do it. The help menu picks which guide shows.
 */
import { Fragment, type ReactNode, type RefObject } from "react";

import { SideDrawer } from "../ui";
import type { HelpGuide } from "./guides";

export interface HelpGuideDrawerProps {
  /** The guide to show; `null` closes the drawer. */
  guide: HelpGuide | null;
  onClose: () => void;
  /** Where focus goes on close when the opener is gone (the Help button). */
  fallbackFocusRef?: RefObject<HTMLElement | null> | undefined;
}

export function HelpGuideDrawer({ guide, onClose, fallbackFocusRef }: HelpGuideDrawerProps) {
  return (
    <SideDrawer
      open={guide !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={guide?.title ?? "Help"}
      description={guide?.summary}
      width="lg"
      testid="help-guide-drawer"
      fallbackFocusRef={fallbackFocusRef}
    >
      {guide ? <HelpGuideBody guide={guide} /> : null}
    </SideDrawer>
  );
}

/** The guide's sections, then a quiet closing line. */
export function HelpGuideBody({ guide }: { guide: HelpGuide }) {
  return (
    <div className="flex flex-col gap-5 p-4" data-testid={`help-guide-${guide.id}-body`}>
      {guide.sections.map((section) => (
        <section key={section.heading} className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-fg">{section.heading}</h3>
          {section.body.map((para) => (
            <p key={para} className="text-sm leading-relaxed text-fg-muted">
              {withBold(para)}
            </p>
          ))}
          {section.steps && section.steps.length > 0 ? (
            <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-sm leading-relaxed text-fg-muted">
              {section.steps.map((step) => (
                <li key={step}>{withBold(step)}</li>
              ))}
            </ol>
          ) : null}
        </section>
      ))}
      <p className="border-t border-border pt-3 text-xs text-fg-subtle">
        More guides are on the way. Tell us what is unclear.
      </p>
    </div>
  );
}

/** `"a **b** c"` → `a <strong>b</strong> c`. Nothing else is parsed. */
export function withBold(text: string): ReactNode {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      // biome-ignore lint/suspicious/noArrayIndexKey: the split's positions are the identity.
      <strong key={i} className="font-semibold text-fg">
        {part}
      </strong>
    ) : (
      // biome-ignore lint/suspicious/noArrayIndexKey: the split's positions are the identity.
      <Fragment key={i}>{part}</Fragment>
    ),
  );
}
